import type { VocabularyEntry } from '@/db/Types'
import { VOCABULARY_CATEGORY_ORDER, VOCABULARY_CATEGORY_LABELS, VOCABULARY_CATEGORY_LABELS_PLURAL } from '@/constants/VocabularyLabels'
import './vocabulary-table.css'

interface VocabularyTableProps {
	entries: VocabularyEntry[]
	groupByCategory: boolean
	isLoading: boolean
	onRowClick: (entry: VocabularyEntry) => void
}

export function VocabularyTable({ entries, groupByCategory, isLoading, onRowClick }: VocabularyTableProps) {
	if (isLoading) {
		return <div className="vocabulary-table__loading">Loading vocabulary...</div>
	}

	if (entries.length === 0) {
		return (
			<div className="vocabulary-table__empty">
				<p>No vocabulary entries yet.</p>
				<p className="vocabulary-table__empty-hint">Create formations, concepts, and modifiers to build your coaching vocabulary.</p>
			</div>
		)
	}

	if (groupByCategory) {
		const grouped = VOCABULARY_CATEGORY_ORDER.map(cat => ({
			category: cat,
			label: VOCABULARY_CATEGORY_LABELS_PLURAL[cat],
			entries: entries.filter(e => e.category === cat),
		})).filter(g => g.entries.length > 0)

		return (
			<div className="vocabulary-table">
				{grouped.map(group => (
					<div key={group.category} className="vocabulary-table__group">
						<div className="vocabulary-table__group-header" data-category={group.category}>
							<span className="vocabulary-table__group-title">{group.label}</span>
							<span className="vocabulary-table__group-count">{group.entries.length}</span>
						</div>
						{group.entries.map((entry, i) => (
							<VocabularyRow key={entry.id} entry={entry} onClick={onRowClick} index={i} />
						))}
					</div>
				))}
			</div>
		)
	}

	return (
		<div className="vocabulary-table">
			{entries.map((entry, i) => (
				<VocabularyRow key={entry.id} entry={entry} onClick={onRowClick} index={i} />
			))}
		</div>
	)
}

function VocabularyRow({ entry, onClick, index }: { entry: VocabularyEntry; onClick: (e: VocabularyEntry) => void; index: number }) {
	return (
		<button
			className="vocabulary-row"
			onClick={() => onClick(entry)}
			style={{ animationDelay: `${index * 50}ms` }}
			type="button"
		>
			<div className="vocabulary-row__thumbnail" />
			<div className="vocabulary-row__code">{entry.code}</div>
			<div className="vocabulary-row__name">{entry.display_name ?? ''}</div>
			<span className="vocabulary-row__badge" data-category={entry.category}>
				{VOCABULARY_CATEGORY_LABELS[entry.category] ?? entry.category}
			</span>
		</button>
	)
}
